<template>
    <div class="table-responsive bg-white rounded-3 mb-0 mt-4">
        <BTable
            id="my-table"
            :items="data"
            :fields="header"
            :per-page="serverSide ? undefined : perPage"
            :current-page="serverSide ? undefined : currentPage"
            show-empty
        >
            <!-- Forward all slots from parent to BTable -->
            <template v-for="(_, slotName) in $slots" v-slot:[slotName]="slotData">
                <slot :name="slotName" v-bind="slotData" />
            </template>

            <template #cell(no)="data">
                {{ (currentPage - 1) * perPage + data.index + 1 }}
            </template>
            <template #cell(is_online)>
                <i class="fas fa-circle" style="color: #38c41c"></i>
            </template>
            <!-- <template #cell(type_room)="props">
                <BBadge
                    class="badge-type-room"
                    :bg-variant="getBadgeTypeRoom(props.item.type_room)"
                >
                    {{ props.item.type_room }}
                </BBadge>
            </template> -->
            <template #cell(status_room)="props">
                <BBadge
                    class="badge-status-room"
                    :bg-variant="getBadgeStatusRoom(props.item.status_room.key)"
                >
                    <img width="16" :src="props.item.status_room.icon" alt="" />
                    {{ props.item.status_room.label }}
                </BBadge>
            </template>
            <template #cell(status)="props">
                <span
                    :class="`badge badge-status text-${getBadgeStatusStaff(
                        props.item.status
                    )} border border-${getBadgeStatusStaff(props.item.status)}`"
                >
                    {{ props.item.status }}
                </span>
            </template>
            <template #cell(status_offer)="props">
                <span
                    :class="`badge badge-status text-${getBadgeStatusOffer(
                        props.item.status
                    )} border border-${getBadgeStatusOffer(props.item.status)}`"
                    class="text-capitalize"
                >
                    {{ props.item.status }}
                </span>
            </template>
            <template #cell(status_extra_bed)="props">
                <span
                    :class="`badge badge-status text-${getBadgeExtraBed(
                        props.item.status_extra_bed
                    )} border border-${getBadgeExtraBed(props.item.status_extra_bed)}`"
                >
                    {{ props.item.status_extra_bed ? 'Yes' : 'No' }}
                </span>
            </template>

            <template #cell(accurate_status)="props">
                <span
                    :class="`badge badge-status text-${getBadgeAccurateStatus(
                        props.item.accurate_status
                    )} border border-${getBadgeAccurateStatus(props.item.accurate_status)}`"
                >
                    {{ props.item.accurate_status }}
                </span>
            </template>
            <template #cell(status_maintenance)="props">
                <BBadge
                    class="badge-status-maintenance"
                    :bg-variant="
                        getBadgeStatusMaintenance(props.item.status_maintenance)
                    "
                >
                    {{ props.item.status_maintenance }}
                </BBadge>
            </template>
            <template #cell(status_reservation)="props">
                <span
                    :class="`badge badge-status text-${getBadgeStatusReservation(
                        props.item.status_reservation.key
                    )} border border-${getBadgeStatusReservation(
                        props.item.status_reservation.key
                    )}`"
                >
                    {{ props.item.status_reservation.label }}
                </span>
            </template>
            <template #thead-top>
                <slot name="thead-top"></slot>
            </template>

            <template #cell(action)="props">
                <div class="d-flex justify-content-center">
                    <BButton
                        v-for="(action, index) in getVisibleActions(props.item)"
                        :key="index"
                        :variant="getButtonVariant(props.item, action)"
                        size="sm"
                        @click="handleAction(props.item, action)"
                        v-tooltip="getLable(props.item, action)"
                        :disabled="handleDisabledAction(props.item, action)"
                        class="mx-1"
                    >
                        <i
                            :class="`${getIcon(props.item, action)} text-white`"
                        ></i>
                    </BButton>
                    <!-- <div class="dropright">
                        <BButton class="btn btn-light mx-2" data-toggle="dropdown" aria-expanded="false">
                        <i class="fas fa-ellipsis-v"></i>
                        </BButton>
                        <div class="dropdown-menu dropdown-menu-right">
                        <a href="" class="dropdown-item-text">Detail</a>
                        </div>
                    </div> -->
                </div>
            </template>
        </BTable>
    </div>
    <div
        class="pagination d-lg-flex d-flex align-items-center justify-content-between bg-white rounded-3"
    >
        <div class="d-lg-flex d-flex align-items-center p-4">
            <div class="pagination-option">
                <BFormSelect v-model="perPage" :options="options" class="">
                </BFormSelect>
            </div>
            <div class="pagination-text">
                <span class="ms-2" style="white-space: nowrap"
                    >Showing {{ displayFrom }} to
                    {{ displayTo }} of
                    {{ totalRowsDisplay }}
                </span>
            </div>
        </div>
        <div class="w-25 p-4">
            <BPagination
                v-model="currentPage"
                :total-rows="serverSide ? totalRowsDisplay : data.length"
                :per-page="perPage"
                aria-controls="my-table"
                class="mb-0 justify-content-end"
                @update:model-value="handlePageChange"
            >
            </BPagination>
        </div>
    </div>
</template>

<script>
import { ref, watch, computed } from "vue";
import {
    BTable,
    BPagination,
    BFormSelect,
    BButton,
    BBadge,
} from "bootstrap-vue-next";

export default {
    name: "DataTable",
    components: {
        BTable,
        BPagination,
        BFormSelect,
        BButton,
        BBadge,
    },
    props: {
        data: Array,
        header: Array,
        rows: Number,
        customActions: Array,
        totalRows: {
            type: Number,
            default: 0,
        },
        serverSide: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["page-change", "per-page-change"],
    setup(props, { emit }) {
        const perPage = ref(10);
        const currentPage = ref(1);
        
        watch(() => props.data, () => {
            if (!props.serverSide) {
                currentPage.value = 1;
            }
        });

        watch(perPage, (value) => {
            if (props.serverSide) {
                currentPage.value = 1;
                emit("per-page-change", value);
            }
        });

        const options = ref([
            {
                text: "10",
                value: 10,
            },
            {
                text: "25",
                value: 25,
            },
            {
                text: "50",
                value: 50,
            },
        ]);
        const getLable = (item, action) => {
            if (typeof action.label === "function") {
                return action.label(item);
            }
            return action.label;
        };
        const getIcon = (item, action) => {
            if (typeof action.icon === "function") {
                return action.icon(item);
            }
            return action.icon;
        };
        const getBadgeExtraBed = (key) => {
            switch (key) {
                case 1:
                    return "success";
                default:
                    return "secondary";
            }
        };
        const getBadgeStatusStaff = (key) => {
            switch (key?.toLowerCase()) {
                case "active":
                    return "success";
                default:
                    return "secondary";
            }
        };
        const getBadgeTypeRoom = (type) => {
            switch (type) {
                case "Deluxe":
                    return "danger";
                case "Super Deluxe":
                    return "light";
                case "Family":
                    return "orange";
                case "Studio":
                    return "success";
                case "Honeymoon":
                    return "primary";
                default:
                    return "";
            }
        };
        const getBadgeStatusOffer = (status) => {
            switch(status) {
                case 'Approved': return 'success';
                case 'Rejected': return 'danger';
                case 'Draft': return 'secondary';
                case 'Pending': return 'warning';
                case 'Completed': return 'primary';
                case 'Cancelled': return 'danger';
                default: return 'info';
            }
        };
        const getBadgeStatusReservation = (status) => {
            switch (status) {
                case "checkin":
                    return "primary";
                case "checkout":
                    return "orange";
                case "checkout_on":
                    return "purple";
                case "inhouse":
                    return "warning";
                case "booked":
                    return "success";
                case "available":
                    return "secondary";
                case "void":
                    return "danger";
                default:
                    return "";
            }
        };
        const getBadgeStatusRoom = (key) => {
            switch (key) {
                case "available":
                    return "light";
                case "reserved":
                    return "success";
                case "Active":
                    return "success";
                case "Accepted":
                    return "success";
                case "checkout":
                    return "orange";
                case "inhouse":
                    return "warning";
                case "checkin":
                    return "primary";
                case "under_maintenance":
                    return "danger";
                case "in_cleaning":
                    return "secondary";
                case "Offered":
                    return "secondary";
                case "dirty":
                    return "purple";
                default:
                    return "secondary";
            }
        };
        const getBadgeStatusMaintenance = (key) => {
            switch (key) {
                case "Under Maintenance":
                    return "danger";
                default:
                    return "success";
            }
        };
        const getBadgeAccurateStatus = (status) => {
            switch (status) {
                case "Synced":
                    return "success";
                default:
                    return "secondary";
            }
        };
        const getButtonVariant = (item, action) => {
            if (item.start_time !== "-" && item.end_time === "-") {
                return "danger";
            }

            if (
                (action.label === "Payment" && item.status_payment === "DP") ||
                (action.label === "Check In" && item.status_payment === "DP") ||
                (action.label === "Update" &&
                    item.status_maintenance === "Maintenance Completed") ||
                item.status_cleaning === "finish"
            ) {
                return "secondary";
            }
            if (typeof action.variant === "function") {
                return action.variant(item);
            }

            return action.variant;
        };
        const handleAction = (item, action) => {
            action.action(item);
        };
        const handleDisabledAction = (item, action) => {
            if (
                (action.label === "Payment" && item.status_payment === "DP") ||
                (action.label === "Check In" && item.status_payment === "DP")
            ) {
                return true; // Disable button
            }
            if (
                action.label === "Finish" &&
                item.status_cleaning === "finish"
            ) {
                return true; // Disable button
            }
            if (
                action.label === "Update" &&
                item.status_maintenance === "Maintenance Completed"
            ) {
                return true; // Disable button
            }
            return false;
        };
        const getVisibleActions = (item) => {
            if (!props.customActions) return [];
            return props.customActions.filter(action => {
                if (typeof action.showIf === 'function') {
                    return action.showIf(item);
                }
                return true; // Show by default if no showIf condition
            });
        };
        const totalRowsDisplay = computed(() => (props.serverSide ? props.totalRows : props.data.length));
        const displayFrom = computed(() => {
            if (totalRowsDisplay.value === 0) return 0;
            return props.serverSide
                ? ((currentPage.value - 1) * perPage.value) + 1
                : ((currentPage.value - 1) * perPage.value) + 1;
        });
        const displayTo = computed(() => {
            if (totalRowsDisplay.value === 0) return 0;
            return props.serverSide
                ? Math.min(currentPage.value * perPage.value, props.totalRows)
                : Math.min(currentPage.value * perPage.value, props.data.length);
        });
        const handlePageChange = (page) => {
            if (props.serverSide) {
                currentPage.value = page;
                emit("page-change", page);
            }
        };
        return {
            perPage,
            options,
            currentPage,
            totalRowsDisplay,
            displayFrom,
            displayTo,
            getBadgeStatusOffer,
            getBadgeStatusStaff,
            getBadgeExtraBed,
            getBadgeTypeRoom,
            getBadgeStatusReservation,
            getBadgeStatusRoom,
            getButtonVariant,
            getBadgeStatusMaintenance,
            getBadgeAccurateStatus,
            handleAction,
            handleDisabledAction,
            getLable,
            getIcon,
            getVisibleActions,
            handlePageChange,
        };
    },
};
</script>

<style>
@media screen and (max-width: 576px) {
    .pagination-option select {
        line-height: 1.25rem;
    }
    .pagination-option option {
        font-size: 0.95rem;
    }
    .pagination-text {
        font-size: 0.85rem;
    }
    .page-item .page-link {
        font-size: 0.75rem;
    }
}
</style>
